<template>
    <div
        class="border rounded-lg border-info px-2 flex flex-col bg-brand-blue py-2"
    >
        <div class="w-full">
            <div v-if="isRealProduct" class="flex flex-col w-full">
                <strong>
                    {{ reference.product.name | cut(50) }}&nbsp;
                    {{
                        reference.active === undefined
                            ? ""
                            : reference.active
                            ? "🟢"
                            : "⚪"
                    }}
                </strong>

                <div>
                    <small>
                        Prod.
                        <router-link
                            :to="`/app/products/detail/${reference.productId}`"
                            class="btn-a p-0"
                        >
                            {{
                                reference.product.sku
                                    ? `SKU ${reference.product.sku}`
                                    : `ID ${reference.productId}`
                            }}
                        </router-link>
                        |
                        <router-link
                            v-if="$ability.hasPermission('inventory:Get')"
                            :to="
                                merchant
                                    ? `/app/inventories/${merchant.merchantType}/${merchant.merchantId}/detail/${reference.id}`
                                    : `/app/inventories/detail/${reference.id}`
                            "
                            class="btn-a p-0"
                        >
                            Ref
                        </router-link>
                        <router-link
                            :to="`/app/products/detail/${reference.productId}?reference=${reference.id}&tab=references`"
                            class="btn-a p-0"
                        >
                            {{
                                reference.barcode
                                    ? `Cód. ${reference.barcode}`
                                    : `ID ${reference.id}`
                            }}
                        </router-link>
                    </small>
                </div>
            </div>
            <div v-else class="flex flex-col w-full">
                <span class="p-0">
                    {{ reference.product.name }}
                </span>
                <small> Servicio / P.A. </small>
            </div>
        </div>
        <div class="flex justify-center p-2">
            <button class="p-0" @click="goToMainPage(reference.product.slug)">
                <img
                    v-img:group
                    v-img-loading
                    class="w-full h-full"
                    :src="reference.images[0].url | image"
                    :alt="reference.images[0].alt"
                    loading="lazy"
                />
            </button>
        </div>
        <div class="flex md:justify-between md:flex-row flex-col items-center">
            <div
                v-if="enabled"
                id="actions"
                class="flex w-full justify-end mt-2 items-center"
            >
                <custom-tooltip v-if="enabledDiscount" border>
                    <custom-checkbox
                        v-model="availableDiscount"
                        warn
                        @input="changeAvailableDiscount"
                    />
                    <template #tooltip> Aplicar Descuento 🤑</template>
                </custom-tooltip>

                <quantity-button
                    v-model="quantity"
                    class="mx-2"
                    max="100"
                    min="1"
                    inline
                    @input="changeQuantity"
                />

                <app-button danger circle icon @click="remove"> 🗑 </app-button>
            </div>
        </div>

        <div class="flex flex-wrap">
            <div
                v-if="reference.color"
                class="w-1/2 flex md:flex-col justify-between pr-2"
            >
                <strong>Color </strong>
                <div class="flex items-center">
                    <small class="capitalize">
                        {{ reference.color }}
                    </small>
                    <div
                        class="ml-1 w-5 h-5 rounded-full border border-theme-border"
                        :style="{
                            'background-color': reference.colorRef
                        }"
                    ></div>
                </div>
            </div>
            <div
                v-if="reference.size"
                class="w-1/2 flex md:flex-col justify-between pr-2"
            >
                <strong>Tamaño</strong>
                <small class="capitalize">
                    {{ reference.size }}
                </small>
            </div>
            <div
                v-if="reference.customReferenceValue"
                class="w-1/2 flex md:flex-col justify-between pr-2"
            >
                <strong>{{
                    reference.product && reference.product.customReferenceTitle
                        ? reference.product.customReferenceTitle
                        : "Variante"
                }}</strong>
                <small class="capitalize">
                    {{ reference.customReferenceValue }}
                </small>
            </div>
            <div class="w-1/2 flex md:flex-col justify-between pr-2">
                <strong>Cantidad </strong>
                <small
                    :class="{
                        'line-through font-bold': quantity === 0
                    }"
                >
                    {{ quantity }}
                </small>
            </div>
            <div class="w-1/2 flex md:flex-col justify-between pr-2">
                <strong>$ Unidad </strong>
                <small>
                    {{ reference.priceOffer | money }}
                </small>
            </div>
            <div
                v-if="Number.isInteger(reference.appliedDiscountPerReference)"
                class="flex w-1/2 md:flex-col justify-between pr-2"
            >
                <strong>$ Descuento u. </strong>
                <small>
                    {{ reference.appliedDiscountPerReference | money }}
                </small>
            </div>

            <div class="w-full mt-2 flex pr-2">
                <strong class="text-theme-primary">
                    $ Variantes: &nbsp;
                </strong>
                <span
                    class="font-bold"
                    :class="{
                        'line-through ': quantity === 0
                    }"
                >
                    {{
                        ((reference.priceOffer -
                            (reference.appliedDiscountPerReference || 0)) *
                            quantity)
                            | money
                    }}
                </span>
            </div>

            <div v-if="reference.cost" class="w-full mt-2 flex pr-2 items-end">
                <span> Costo: &nbsp; </span>
                <small>
                    {{ reference.cost | money }}
                </small>
            </div>
            <div
                v-if="reference.priceAgreedMerchant"
                class="w-full mt-2 flex pr-2 items-end"
            >
                <span> $ Acordado con tienda: &nbsp; </span>
                <small>
                    {{ reference.priceAgreedMerchant | money }}
                </small>
            </div>
            <div
                v-if="reference.priceOfferMerchant"
                class="w-full mt-2 flex pr-2 items-end"
            >
                <span> $ Venta en tienda: &nbsp; </span>
                <small>
                    {{ reference.priceOfferMerchant | money }}
                </small>
            </div>
            <div
                v-if="
                    reference.stockMerchant &&
                    $ability.hasPermission('inventory:Get')
                "
                class="w-full my-2 flex pr-2 items-end"
            >
                <span> Unidades en tienda: &nbsp; </span>
                <small>
                    {{ reference.stockMerchant }}
                </small>
            </div>
        </div>

        <div v-if="availableDiscount && enabled" class="mt-4 pr-2">
            <input-money-component
                v-model="appliedDiscountPerReference"
                enabled
                label="Descuento neto por referencia"
                icon="💲"
                :message-error="
                    appliedDiscountPerReference > reference.priceOffer * 0.6
                        ? 'Descuento no debe exceder el 60% del valor de la referencia'
                        : ''
                "
                @blur="blurAppliedDiscountPerReference"
                @focus="focusInput"
            />
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";

import { DEFAULT_IMAGE_URL } from "@/common/constants";

import InputMoneyComponent from "@/components/utils/InputMoneyComponent.vue";
import QuantityButton from "@/components/utils/QuantityButton.vue";

export default {
    name: "ReferenceTableItem2",
    components: { InputMoneyComponent, QuantityButton },
    props: {
        reference: {
            type: Object,
            required: true,
            default: () => ({
                id: 20,
                size: "pequeño",
                color: "rojo",
                colorRef: "#f00",
                quantity: 1,
                price: 10000,
                priceOffer: 10000,
                priceAgreed: 1000,
                appliedDiscountPerReference: 0,
                images: [
                    {
                        id: 1,
                        url: DEFAULT_IMAGE_URL,
                        alt: "alt"
                    }
                ],
                product: {
                    id: "",
                    name: "Cargando",
                    summary: "Cargando..."
                }
            })
        },
        enabled: {
            default: () => false,
            type: Boolean,
            required: false
        },
        enabledDiscount: {
            type: Boolean,
            required: false,
            default: () => false
        },
        merchant: {
            type: Object,
            required: false,
            default: () => null
        }
    },
    emits: ["update", "remove", "focus"],
    data: () => ({
        isEditingName: false,
        editedProductName: "",
        quantity: 0,
        appliedDiscountPerReference: 0,
        inventories: [],
        availableDiscount: false,
        stockActive: 0
    }),
    computed: {
        isRealProduct() {
            return (
                "id" in this.reference.product &&
                !!this.reference.product.id &&
                !!this.reference.id
            );
        }
    },
    watch: {
        reference(value) {
            this.setInput(value);
        }
    },
    mounted() {
        this.setInput(this.reference);
    },
    methods: {
        ...mapActions("inventories", ["listInventoryByReference"]),
        goToMainPage(slug) {
            if (this.reference.active) {
                window.open(`${this.$publicUrl}/${slug}`, "_blank").focus();
            }
        },
        remove() {
            this.$emit("remove", { id: this.reference.id });
        },
        blurAppliedDiscountPerReference() {
            this.$emit("update", {
                id: this.reference.id,
                quantity: this.quantity,
                appliedDiscountPerReference: this.appliedDiscountPerReference
            });
        },
        focusInput() {
            this.$emit("focus");
        },
        setInput(value) {
            this.quantity = Number(value.quantity);
            this.appliedDiscountPerReference =
                Number(value.appliedDiscountPerReference) || 0;
            this.availableDiscount = this.appliedDiscountPerReference > 0;
        },
        changeQuantity(value) {
            this.$emit("update", {
                id: this.reference.id,
                quantity: value,
                appliedDiscountPerReference: this.appliedDiscountPerReference,
                product: this.reference.product
            });
        },
        changeAvailableDiscount(value) {
            if (!value) {
                this.appliedDiscountPerReference = 0;
                this.blurAppliedDiscountPerReference();
            }
        },
        editProductName() {
            this.isEditingName = true;
        },
        saveProductName() {
            if (this.editedProductName.trim() !== "") {
                this.$emit("update", {
                    ...this.reference,
                    product: {
                        ...this.reference.product,
                        name: this.editedProductName
                    }
                });
            }
            this.isEditingName = false;
        }
    }
};
</script>
